// src/hooks/useScrollDirection.js
import { useEffect, useRef, useState } from "react";

/**
 * Tracks scroll direction and top position.
 *
 * Returns:
 * - isScrollingUp: true if user is scrolling up
 * - isAtTop: true if scrollY === 0
 */
export const useScrollDirection = (scrollContainerRef) => {
	const [isScrollingUp, setIsScrollingUp] = useState(true);
	const [isAtTop, setIsAtTop] = useState(true);

	const lastScrollY = useRef(0);
	// Mirror the last emitted UI flags so scroll events can compare against refs
	// first and skip React state updates when nothing user-visible changed.
	const isScrollingUpRef = useRef(true);
	const isAtTopRef = useRef(true);

	useEffect(() => {
		const container = scrollContainerRef?.current ?? window;
		const readScrollOffset = () =>
			container === window ? window.scrollY : (container?.scrollTop ?? 0);

		const handleScroll = () => {
			const currentY = readScrollOffset();
			const nextIsAtTop = currentY === 0;
			// Keep the previous direction when scrollY is unchanged to avoid
			// flipping the header state on inert/no-op scroll events.
			const nextIsScrollingUp =
				currentY < lastScrollY.current
					? true
					: currentY > lastScrollY.current
						? false
						: isScrollingUpRef.current;

			// Avoid state updates when the derived scroll flags have not changed;
			// the header only needs to re-render when visibility state actually flips.
			if (nextIsAtTop !== isAtTopRef.current) {
				isAtTopRef.current = nextIsAtTop;
				setIsAtTop(nextIsAtTop);
			}
			if (nextIsScrollingUp !== isScrollingUpRef.current) {
				isScrollingUpRef.current = nextIsScrollingUp;
				setIsScrollingUp(nextIsScrollingUp);
			}

			lastScrollY.current = currentY;
		};

		handleScroll(); // initialize
		container.addEventListener("scroll", handleScroll, { passive: true });

		return () => container.removeEventListener("scroll", handleScroll);
	}, [scrollContainerRef]);

	return { isScrollingUp, isAtTop };
};
